import { KeyRound, LogOut, Moon, Settings, ShieldCheck, Sun } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Badge } from '../components/ui/Badge';
import { http } from '../api/client';
import { useAuth } from '../store/auth';
import { useUi } from '../store/ui';
import { initials, colorFor } from '../lib/format';

/** Profile popover: role, permissions count, theme, password change, sign out. */
export function UserMenu({ onClose }: { onClose: () => void }) {
  const user = useAuth((s) => s.user);
  const signOut = useAuth((s) => s.signOut);
  const can = useAuth((s) => s.can);
  const { theme, setTheme } = useUi();
  const toast = useUi((s) => s.toast);
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ currentPassword: '', newPassword: '' });

  // Close on outside click or Escape.
  useEffect(() => {
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const changePassword = async () => {
    setBusy(true);
    try {
      await http.post('/auth/change-password', form);
      toast({ kind: 'success', title: 'Password changed', message: 'Every other session has been signed out.' });
      setPwOpen(false);
      setForm({ currentPassword: '', newPassword: '' });
    } catch (err) {
      toast({ kind: 'error', title: 'Could not change password', message: (err as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const Item = ({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) => (
    <button
      onClick={onClick}
      className="flex min-h-touch w-full items-center gap-3 rounded-lg px-3 py-2 text-start text-sm text-ink transition-colors hover:bg-surface-2"
    >
      {icon}
      {label}
    </button>
  );

  return (
    <>
      <div
        ref={ref}
        role="menu"
        className="absolute end-3 top-[68px] z-40 w-72 animate-fade-in rounded-xl border border-line bg-surface-1 p-2 shadow-pop sm:end-6"
      >
        <div className="flex items-center gap-3 rounded-lg px-3 py-2">
          <span
            className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold text-white"
            style={{ backgroundColor: colorFor(user?.roleKey ?? 'u') }} aria-hidden
          >
            {initials(user?.fullName ?? '?')}
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-ink">{user?.fullName}</p>
            <p className="truncate text-xs text-ink-faint">{user?.email ?? user?.username}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 border-y border-line px-3 py-2">
          <Badge tone="brand">{user?.roleName}</Badge>
          <Badge>{user?.permissions.length ?? 0} permissions</Badge>
          {user?.branchName && <Badge tone="neutral">{user.branchName}</Badge>}
        </div>

        <div className="py-1">
          <Item icon={<ShieldCheck className="h-4 w-4 text-ink-faint" />} label="Your permissions"
            onClick={() => { navigate('/roles'); onClose(); }} />
          {can('settings:manage') && (
            <Item icon={<Settings className="h-4 w-4 text-ink-faint" />} label="Settings"
              onClick={() => { navigate('/settings'); onClose(); }} />
          )}
          <Item icon={<KeyRound className="h-4 w-4 text-ink-faint" />} label="Change password"
            onClick={() => { setPwOpen(true); onClose(); }} />
          <Item
            icon={theme === 'dark' ? <Sun className="h-4 w-4 text-ink-faint" /> : <Moon className="h-4 w-4 text-ink-faint" />}
            label={theme === 'dark' ? 'Light theme' : 'Dark theme'}
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          />
        </div>

        <div className="border-t border-line pt-1">
          <Item
            icon={<LogOut className="h-4 w-4 text-red-500" />} label="Sign out"
            onClick={() => { void signOut(); navigate('/login'); }}
          />
        </div>
      </div>

      <Modal
        open={pwOpen} onClose={() => setPwOpen(false)} title="Change password" size="sm"
        description="Changing your password signs you out of every other device."
        footer={
          <>
            <Button variant="secondary" onClick={() => setPwOpen(false)}>Cancel</Button>
            <Button loading={busy} onClick={() => void changePassword()}>Update password</Button>
          </>
        }
      >
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); void changePassword(); }}>
          <Input
            label="Current password" type="password" autoComplete="current-password" required
            value={form.currentPassword}
            onChange={(e) => setForm({ ...form, currentPassword: e.target.value })}
          />
          <Input
            label="New password" type="password" autoComplete="new-password" required minLength={8}
            hint="At least 8 characters."
            value={form.newPassword}
            onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
          />
        </form>
      </Modal>
    </>
  );
}
